/**
 * The Apps tab's Config for one service (`GET /app-config/services/<name>`): the names of its variables, its secrets
 * (set or still to set) and its scheduled jobs. Never a value: configNames keeps the names only. "Open Config" opens
 * the full tab, where they are changed.
 */
import React from 'react';
import PropTypes from 'prop-types';
import { getServiceConfig } from '../../../services/AppConfigService';
import { configNames } from '../../../utils/serviceTabsModel';
import { Card, LoadState, OpenLink, useLoaded } from './common';

export default function ConfigTab({ projectId, service, onOpen = null }) {
  const state = useLoaded(() => getServiceConfig(projectId, service), [projectId, service]);
  const { vars, secrets, jobs } = configNames(state.value);
  const ready = !state.loading && !state.error;
  return (
    <div className="apps-tab-body" data-testid="apps-tabpanel-config">
      <div className="apps-infos">
        <Card testId="apps-config-vars" title="Variables">
          {!ready ? <LoadState state={state} /> : vars.length === 0 ? <p className="apps-muted">No variables.</p> : (
            <ul className="apps-names">{vars.map((name) => <li key={name}><code>{name}</code></li>)}</ul>
          )}
        </Card>
        <Card testId="apps-config-secrets" title="Secrets">
          {!ready ? <LoadState state={state} /> : secrets.length === 0 ? <p className="apps-muted">No secrets.</p> : (
            <ul className="apps-rows">
              {secrets.map((secret) => (
                <li key={secret.name}>
                  <code>{secret.name}</code>
                  {secret.file
                    ? <span className="apps-muted" data-testid={`apps-config-file-${secret.name}`}>file{typeof secret.file === 'string' ? ` ${secret.file}` : ''}, its path{secret.envName ? ` also in ${secret.envName}` : ''}</span>
                    : secret.set ? <span className="apps-muted">set</span> : <strong className="apps-warn">to set</strong>}
                </li>
              ))}
            </ul>
          )}
        </Card>
        <Card testId="apps-config-jobs" title="Scheduled jobs" wide>
          {!ready ? <LoadState state={state} /> : jobs.length === 0 ? <p className="apps-muted">No scheduled jobs.</p> : (
            <ul className="apps-rows">
              {jobs.map((job) => (
                <li key={job.name}>
                  <span className="apps-row-name">{job.name}{!job.enabled && <span className="apps-muted"> (paused)</span>}</span>
                  <span className="apps-muted">{job.when}{job.lastStatus === 'failed' && <> · <strong className="apps-bad">last run failed</strong></>}</span>
                </li>
              ))}
            </ul>
          )}
        </Card>
      </div>
      <OpenLink label="Open Config" onOpen={onOpen} testId="apps-open-config" />
    </div>
  );
}

ConfigTab.propTypes = { onOpen: PropTypes.func, projectId: PropTypes.string, service: PropTypes.string.isRequired };
